import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { formatName } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Search, Sparkles, ArrowUpRight, Trash2, ShieldAlert, ClipboardPaste, FileSpreadsheet, MoreHorizontal } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import NewClientDialog from "@/components/NewClientDialog";
import DuplicatesDialog from "@/components/DuplicatesDialog";
import ImportContactFromTextDialog from "@/components/ImportContactFromTextDialog";
import ImportContactFromExcelDialog from "@/components/ImportContactFromExcelDialog";
import { toast } from "sonner";

export default function ClientsPage() {
  const { t } = useI18n();
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [openNew, setOpenNew] = useState(false);
  const [openAI, setOpenAI] = useState(false);
  const [openDup, setOpenDup] = useState(false);
  const [openText, setOpenText] = useState(false);
  const [openExcel, setOpenExcel] = useState(false);
  const [dupCount, setDupCount] = useState(0);
  const navigate = useNavigate();

  const load = async () => {
    const res = await api.get("/clients", { params: q ? { q } : {} });
    setClients(res.data);
    // refresh duplicate count
    try {
      const d = await api.get("/clients/duplicates/scan");
      setDupCount(d.data.total_groups || 0);
    } catch { /* ignore */ }
  };

  const deleteClient = async (e, c) => {
    e.stopPropagation();
    if (!window.confirm(`Löschen ${c.first_name} ${c.last_name}?\nWerden rimossi anche Policen, Dokumente e formulari del Kunde.`)) return;
    try {
      await api.delete(`/clients/${c.id}`);
      toast.success("Kunde gelöscht");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    const h = setTimeout(load, 300);
    return () => clearTimeout(h);
    // eslint-disable-next-line
  }, [q]);

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="clients-page">
      {/* Title bar — stack on mobile */}
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="label-uppercase text-prime">/ {t.clients}</div>
          <h1 className="font-display font-extrabold text-2xl sm:text-4xl lg:text-5xl tracking-tight mt-1">{t.clients}</h1>
        </div>

        {/* Desktop action bar — visible only ≥ md */}
        <div className="hidden md:flex gap-2 flex-wrap">
          <Button
            onClick={() => setOpenAI(true)}
            className="btn-prime gap-2"
            data-testid="new-client-ai-btn"
          >
            <Sparkles className="w-4 h-4" /> {t.add_client_ai}
          </Button>
          <Button
            onClick={() => setOpenText(true)}
            variant="outline"
            className="gap-2 border-prime/40 text-prime hover:bg-prime/5"
            data-testid="import-text-btn"
          >
            <ClipboardPaste className="w-4 h-4" /> Aus Text
          </Button>
          <Button
            onClick={() => setOpenExcel(true)}
            variant="outline"
            className="gap-2 border-prime/40 text-prime hover:bg-prime/5"
            data-testid="import-excel-btn"
          >
            <FileSpreadsheet className="w-4 h-4" /> Aus Excel
          </Button>
          <Button
            onClick={() => setOpenNew(true)}
            variant="outline"
            className="gap-2"
            data-testid="new-client-btn"
          >
            <Plus className="w-4 h-4" /> {t.add_client}
          </Button>
          <Button
            onClick={() => setOpenDup(true)}
            variant="outline"
            className={`gap-2 ${dupCount > 0 ? "border-prime text-prime" : ""}`}
            data-testid="duplicates-btn"
          >
            <ShieldAlert className="w-4 h-4" /> Duplikate
            {dupCount > 0 && (
              <span className="ml-1 text-[10px] font-bold bg-prime text-white px-1.5 py-0.5 rounded">
                {dupCount}
              </span>
            )}
          </Button>
        </div>

        {/* Mobile compact action bar — only < md */}
        <div className="md:hidden flex gap-2 items-center">
          <Button
            onClick={() => setOpenAI(true)}
            className="btn-prime gap-1.5 h-9 px-3"
            data-testid="new-client-ai-btn-mobile"
          >
            <Sparkles className="w-4 h-4" /> <span className="text-xs">KI</span>
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="h-9 w-9 relative"
                data-testid="clients-more-actions-btn"
              >
                <MoreHorizontal className="w-4 h-4" />
                {dupCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-prime text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                    {dupCount > 9 ? "9+" : dupCount}
                  </span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={() => setOpenNew(true)} data-testid="new-client-btn-mobile">
                <Plus className="w-4 h-4 mr-2" /> {t.add_client}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setOpenText(true)} data-testid="import-text-btn-mobile">
                <ClipboardPaste className="w-4 h-4 mr-2" /> Aus Text
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setOpenExcel(true)} data-testid="import-excel-btn-mobile">
                <FileSpreadsheet className="w-4 h-4 mr-2" /> Aus Excel
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setOpenDup(true)} data-testid="duplicates-btn-mobile">
                <ShieldAlert className="w-4 h-4 mr-2" /> Duplikate
                {dupCount > 0 && (
                  <span className="ml-auto text-[10px] font-bold bg-prime text-white px-1.5 py-0.5 rounded">
                    {dupCount}
                  </span>
                )}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="prime-card p-1">
        <div className="flex items-center gap-2 px-3 sm:px-4 py-2 border-b border-border">
          <Search className="w-4 h-4 text-muted-foreground shrink-0" />
          <Input
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder={t.search_clients}
            className="border-0 focus-visible:ring-0 px-0 rounded-none"
            data-testid="search-clients"
          />
        </div>
        {clients.length === 0 ? (
          <div className="p-16 text-center text-sm text-muted-foreground">{t.no_clients}</div>
        ) : (
          <div className="divide-y divide-border">
            {clients.map(c => (
              <div
                key={c.id}
                onClick={() => navigate(`/clients/${c.id}`)}
                className="flex items-center gap-3 sm:gap-4 px-3 sm:px-6 py-3 sm:py-4 hover:bg-secondary active:bg-secondary cursor-pointer group transition-colors"
                data-testid={`client-row-${c.id}`}
              >
                <div className="w-11 h-11 sm:w-10 sm:h-10 bg-foreground text-background flex items-center justify-center font-display font-bold text-sm rounded-md shrink-0">
                  {(c.first_name?.[0] || "").toUpperCase()}{(c.last_name?.[0] || "").toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm sm:text-sm truncate">{formatName(c.first_name)} {formatName(c.last_name)}</div>
                  <div className="text-xs text-muted-foreground font-mono truncate">
                    {c.email || "—"}{c.city ? <> · {c.city}</> : null}
                  </div>
                </div>
                <Button
                  variant="ghost" size="icon"
                  className="hidden sm:flex opacity-0 group-hover:opacity-100 transition-opacity hover:text-destructive shrink-0"
                  onClick={(e) => deleteClient(e, c)}
                  data-testid={`delete-client-${c.id}`}
                  title="Kunde löschen"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
                <ArrowUpRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground shrink-0" />
              </div>
            ))}
          </div>
        )}
      </div>

      <NewClientDialog open={openNew} onOpenChange={setOpenNew} aiMode={false} onCreated={(c) => { load(); navigate(`/clients/${c.id}`); }} />
      <NewClientDialog open={openAI} onOpenChange={setOpenAI} aiMode={true} onCreated={(c) => { load(); navigate(`/clients/${c.id}`); }} />
      <ImportContactFromTextDialog open={openText} onOpenChange={setOpenText} onCreated={(c) => { load(); navigate(`/clients/${c.id}`); }} />
      <ImportContactFromExcelDialog open={openExcel} onOpenChange={setOpenExcel} onImported={() => load()} />
      <DuplicatesDialog open={openDup} onOpenChange={setOpenDup} onResolved={load} />
    </div>
  );
}
